import { useEffect, useState } from "react";
import {
  HOW_YOU_CHARGE,
  SETUP_QUESTIONS,
  answersAreReady,
  calendarPreviewFromAnswers,
  capabilitiesFromAnswers,
  generatedCalendarGuide,
  generatedSetupExample,
  generatedSetupLines,
  normalizeClubCapabilityAnswers,
  toggleCollectAnswer,
  togglePayAnswer,
} from "../../util/clubCapabilities";

const QUESTIONS = ["pay", "bookers", "coaching", "collect"];
const REVIEW = QUESTIONS.length;

const optionTestId = (questionId, value) => `capability-${questionId}-${String(value)}`;

const isStepReady = (step, answers) => {
  if (step === 0) {
    return answers.pay.length > 0;
  }
  if (step === 1) {
    return Boolean(answers.bookers);
  }
  if (step === 2) {
    return answers.coaching !== null;
  }
  if (step === 3) {
    return answers.collect.length > 0;
  }
  return answersAreReady(answers);
};

const OptionButton = ({ selected, testId, label, hint, onClick, multiple = false }) => (
  <button
    type="button"
    className={[
      "club-choice",
      multiple ? "is-multiple" : "is-single",
      selected ? "is-selected" : "",
    ].filter(Boolean).join(" ")}
    role={multiple ? "checkbox" : "radio"}
    aria-checked={selected}
    data-testid={testId}
    onClick={onClick}
  >
    <span className="club-choice-mark" aria-hidden="true">{selected ? "✓" : ""}</span>
    <span className="club-choice-copy">
      <span className="club-choice-label">{label}</span>
      <span className="club-choice-hint">{hint}</span>
    </span>
  </button>
);

const CalendarPreview = ({ answers }) => {
  const preview = calendarPreviewFromAnswers(answers);
  return (
    <aside className="club-calendar-preview" data-testid="calendar-preview">
      <p className="setup-guide-kicker">{HOW_YOU_CHARGE.previewKicker}</p>
      {preview.pending ? (
        <p className="club-calendar-preview-why">{preview.why}</p>
      ) : (
        <>
          <h3 className="club-calendar-preview-name">{preview.name}</h3>
          <p className="club-calendar-preview-why">{preview.why}</p>
          {preview.sidebar && (
            <p className="club-calendar-preview-sidebar">{preview.sidebar}</p>
          )}
          {preview.fit && (
            <p className="club-calendar-preview-fit" data-testid="calendar-preview-fit">
              <strong>Best for</strong>
              {preview.fit}
            </p>
          )}
        </>
      )}
    </aside>
  );
};

const ClubCapabilitiesSetup = ({
  answers,
  onAnswersChange,
  reviewing = false,
  onReviewingChange,
}) => {
  const current = normalizeClubCapabilityAnswers(answers);
  const [step, setStep] = useState(reviewing ? REVIEW : 0);
  const generated = capabilitiesFromAnswers(current);
  const lines = generatedSetupLines(generated, current);
  const calendarGuide = generatedCalendarGuide(generated);
  const reviewExample = generatedSetupExample(current);

  useEffect(() => {
    if (reviewing) {
      setStep(REVIEW);
      return;
    }
    setStep((currentStep) => (currentStep === REVIEW ? QUESTIONS.length - 1 : currentStep));
  }, [reviewing]);

  const setAnswers = (next) => {
    if (typeof onAnswersChange === "function") {
      onAnswersChange(next);
    }
  };

  const goReview = () => {
    if (typeof onReviewingChange === "function") {
      onReviewingChange(true);
    }
    setStep(REVIEW);
  };

  const handleContinue = () => {
    if (step >= QUESTIONS.length - 1) {
      goReview();
      return;
    }
    setStep(step + 1);
  };

  const handleQuestionBack = () => {
    if (step <= 0) {
      return;
    }
    setStep(step - 1);
  };

  if (step === REVIEW) {
    return (
      <section className="setup-guide club-interview" data-testid="how-you-charge">
        <p className="setup-guide-kicker">{HOW_YOU_CHARGE.kicker}</p>
        <h2 className="setup-guide-title">{HOW_YOU_CHARGE.nextLabel}</h2>
        <p className="setup-guide-lead">{HOW_YOU_CHARGE.reviewHelp}</p>
        <CalendarPreview answers={current} />
        {reviewExample && (
          <p className="club-interview-example" data-testid="how-you-charge-example">
            <strong>Example</strong>
            {reviewExample}
          </p>
        )}
        <div data-testid="how-you-charge-next">
          {lines.length > 0 ? (
            <ul className="club-generated-list" data-testid="generated-setup">
              {lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : (
            <p className="setup-guide-body">{HOW_YOU_CHARGE.empty}</p>
          )}
        </div>
        {calendarGuide.length > 0 && (
          <div className="club-calendar-guide" data-testid="calendar-setup-guide">
            <h3 className="setup-guide-label">{HOW_YOU_CHARGE.calendarLabel}</h3>
            <ul className="club-generated-list">
              {calendarGuide.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="setup-guide-hint">{HOW_YOU_CHARGE.afterThis}</p>
      </section>
    );
  }

  const questionKey = QUESTIONS[step];
  const question = SETUP_QUESTIONS[questionKey];
  const ready = isStepReady(step, current);

  return (
    <section className="setup-guide club-interview" data-testid="how-you-charge">
      <p className="setup-guide-kicker">{HOW_YOU_CHARGE.kicker}</p>
      <p className="club-interview-progress">{HOW_YOU_CHARGE.questionOf(step + 1, QUESTIONS.length)}</p>
      <h2 className="setup-guide-title">{question.prompt}</h2>
      <p className="setup-guide-lead">{question.help}</p>
      {question.example && (
        <p className="club-interview-example" data-testid="how-you-charge-example">
          <strong>Example</strong>
          {question.example}
        </p>
      )}

      <div className="club-choice-list" role={questionKey === "pay" || questionKey === "collect" ? "group" : "radiogroup"}>
        {questionKey === "pay" && question.options.map((option) => (
          <OptionButton
            key={option.id}
            multiple
            selected={current.pay.includes(option.id)}
            testId={optionTestId("pay", option.id)}
            label={option.label}
            hint={option.hint}
            onClick={() => setAnswers({ ...current, pay: togglePayAnswer(current.pay, option.id) })}
          />
        ))}
        {questionKey === "bookers" && question.options.map((option) => (
          <OptionButton
            key={option.id}
            selected={current.bookers === option.id}
            testId={optionTestId("bookers", option.id)}
            label={option.label}
            hint={option.hint}
            onClick={() => setAnswers({ ...current, bookers: option.id })}
          />
        ))}
        {questionKey === "coaching" && question.options.map((option) => (
          <OptionButton
            key={String(option.id)}
            selected={current.coaching === option.id}
            testId={optionTestId("coaching", option.id ? "yes" : "no")}
            label={option.label}
            hint={option.hint}
            onClick={() => setAnswers({ ...current, coaching: option.id })}
          />
        ))}
        {questionKey === "collect" && question.options.map((option) => (
          <OptionButton
            key={option.id}
            multiple
            selected={current.collect.includes(option.id)}
            testId={optionTestId("collect", option.id)}
            label={option.label}
            hint={option.hint}
            onClick={() => setAnswers({
              ...current,
              collect: toggleCollectAnswer(current.collect, option.id),
            })}
          />
        ))}
      </div>

      <CalendarPreview answers={current} />

      <div className="club-interview-actions">
        <button
          type="button"
          className="club-interview-back"
          disabled={step === 0}
          onClick={handleQuestionBack}
          data-testid="how-you-charge-question-back"
        >
          Back
        </button>
        <button
          type="button"
          className="club-interview-continue"
          disabled={!ready}
          onClick={handleContinue}
          data-testid="how-you-charge-continue"
        >
          {HOW_YOU_CHARGE.continueLabel}
        </button>
      </div>
    </section>
  );
};

export default ClubCapabilitiesSetup;
